import { Box, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, TablePagination, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Edit, Delete, Visibility, Publish } from '@mui/icons-material';
import { News } from '../../types';

interface NewsTableProps {
  news: News[];
  loading: boolean;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onEdit: (news: News) => void;
  onDelete: (id: number) => void;
  onPublish: (id: number) => void;
  onUnpublish: (id: number) => void;
}

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    DRAFT: 'Черновик',
    PUBLISHED: 'Опубликовано',
    HIDDEN: 'Скрыто',
    ARCHIVED: 'Архив',
  };
  return labels[status] || status;
};

const getStatusColor = (status: string) => {
  const colors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
    DRAFT: 'default',
    PUBLISHED: 'success',
    HIDDEN: 'warning',
    ARCHIVED: 'error',
  };
  return colors[status] || 'default';
};

// The column used to print createdAt — the import stamp, identical for the whole
// migrated corpus. The date of the material is date_iso, which is also what the
// public site shows and orders by; createdAt only stands in when it is empty.
// Read in UTC: date_iso is a `date` column served as UTC midnight.
const formatNewsDate = (item: News): string => {
  const raw = item.dateIso || item.createdAt;
  if (!raw) return '-';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '-';
  return item.dateIso
    ? date.toLocaleDateString('ru-RU', { timeZone: 'UTC' })
    : date.toLocaleDateString('ru-RU');
};

export function NewsTable({ news, loading, total, page, limit, onPageChange, onEdit, onDelete, onPublish, onUnpublish }: NewsTableProps) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Дата</TableCell>
            <TableCell>Заголовок</TableCell>
            <TableCell>Автор</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {news.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell>{formatNewsDate(item)}</TableCell>
              <TableCell>{item.title}</TableCell>
              <TableCell>{item.author?.name || '-'}</TableCell>
              <TableCell>
                <Chip 
                  label={getStatusLabel(item.status)} 
                  size="small" 
                  color={getStatusColor(item.status)}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Просмотр">
                    <IconButton size="small" onClick={() => onEdit(item)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Редактировать">
                    <IconButton size="small" onClick={() => onEdit(item)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {item.status !== 'PUBLISHED' && (
                    <Tooltip title="Опубликовать">
                      <IconButton size="small" onClick={() => onPublish(item.id)}>
                        <Publish fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {item.status === 'PUBLISHED' && (
                    <Tooltip title="Снять с публикации">
                      <IconButton size="small" onClick={() => onUnpublish(item.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Удалить">
                    <IconButton size="small" onClick={() => onDelete(item.id)} sx={{ color: 'error.main' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {news.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography color="text.secondary">Новости не найдены</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={limit}
        onRowsPerPageChange={() => {}}
        rowsPerPageOptions={[]}
      />
    </TableContainer>
  );
}

interface NewsToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
}

export function NewsToolbar({ search, onSearchChange, statusFilter, onStatusFilterChange }: NewsToolbarProps) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
      <TextField
        label="Поиск"
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ minWidth: 200 }}
      />
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Статус</InputLabel>
        <Select
          value={statusFilter}
          label="Статус"
          onChange={(e) => onStatusFilterChange(e.target.value as string)}
        >
          <MenuItem value="">Все</MenuItem>
          <MenuItem value="DRAFT">Черновик</MenuItem>
          <MenuItem value="PUBLISHED">Опубликовано</MenuItem>
          <MenuItem value="HIDDEN">Скрыто</MenuItem>
          <MenuItem value="ARCHIVED">Архив</MenuItem>
        </Select>
      </FormControl>
      {(search || statusFilter) && (
        <Box sx={{ borderLeft: '1px solid #ccc', ml: 1, pl: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Активны фильтры
          </Typography>
        </Box>
      )}
    </Box>
  );
}
